import {
  type ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { UNSAFE_DataRouterContext, useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useGetOrgGatesQuery, useGetUserGatesQuery } from '@features/admin-v2/store/api';
import {
  ArtifactDropOverlay,
  formatDuplicateMessage,
  formatUploadLimitError,
  PendingUploadCard,
  useAdminArtifactUploadConstraints,
  useAdminOrganizationArtifactEventStream,
  useChartWideDropZone,
  useOptimisticUploads,
  useUploadAdminOrganizationArtifacts,
  validateFiles,
} from '@features/patient-artifacts';
import { AdminPayerConnections } from '@features/payer-connections/components/AdminPayerConnections';
import { Chip } from '@jotpsych/ui';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { FeatureGates, useFeatureGate } from '@services/statsig';
import { ROLES } from '@utils/roleUtils';
import { useSnackbar } from 'notistack';

import { AdminIntakeNavigationGuard } from './components/AdminIntakeNavigationGuard';
import { AdminCredentialingStatuses } from './components/credentialing/AdminCredentialingStatuses';
import { AdminIntakeVerification } from './components/four-stage/AdminIntakeVerification';
import { IntakeAgreementPanel } from './components/four-stage/IntakeAgreementPanel';
import { journeyStageLocation, resolveJourneyStage } from './components/four-stage/journeyRouting';
import { LiveIntakeJourney } from './components/four-stage/LiveIntakeJourney';
import { IntakeHubDashboard } from './components/IntakeHubDashboard';
import {
  IntakeHubOrgServiceEnrollmentControl,
  type IntakeHubOrgServiceOption,
} from './components/IntakeHubOrgServiceEnrollmentControl';
import { IntakeExperience } from './components/shell/IntakeExperience';
import { intakeAgreementScope, useAdminIntakeAgreement } from './hooks/useIntakeAgreement';
import {
  useGetAdminIntakeHubDashboardQuery,
  useGetAdminIntakeHubDefinitionsQuery,
  useGetAdminIntakeHubDocumentsQuery,
  useGetAdminIntakeHubOrgEnrollmentsQuery,
  useSetAdminIntakeHubOrgEnrollmentsMutation,
} from './store/api';
import {
  resolveAdminProviderScope,
  resolveAdminStatusTab,
  safeIntakeReturnTo,
} from './adminJourneyRouting';
import { documentInventoryPollingInterval } from './documentPolling';
import { hasLiveProviderEnrollment } from './enrollmentScope';
import { intakeHubDashboardResponseToScenario } from './mappers';
import { hasIntakeV2DevelopmentOverride } from './routing';
import type { IntakeItem, IntakeServiceId } from './types';

interface AdminIntakeHubProvider {
  provider_id?: string;
  user_id?: string;
  name?: string;
  email?: string;
  roles?: string[];
}

interface AdminIntakeHubEmbeddedProps {
  organizationId: string;
  providers?: AdminIntakeHubProvider[];
  isActive?: boolean;
}

/**
 * A lost optimistic-concurrency race on the organization enrollment write.
 *
 * The 409 body is a plain `ClientErrorResponse` — status, error code, message —
 * so there is no persisted service list to read out of it. Recognise the shape
 * and re-read the set instead.
 */
function isWriteConflict(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const status = (error as { status?: unknown }).status;
  if (status === 409) return true;
  const data = (error as { data?: { error_code?: unknown } }).data;
  return data?.error_code === 'intake_item_write_conflict';
}

function getProviderId(provider: AdminIntakeHubProvider) {
  return provider.user_id || provider.provider_id || '';
}

export function AdminIntakeHubEmbedded(props: AdminIntakeHubEmbeddedProps) {
  const { user, isAuthenticated, isLoading } = useAuth0();
  const roles: unknown = user?.['https://api.smartscribe.health/roles'];
  if (isLoading) return <LinearProgress aria-label="Loading staff access" />;
  if (
    !isAuthenticated ||
    !Array.isArray(roles) ||
    !roles.some(role => role === 'Admin' || role === 'Moderator')
  )
    return <Alert severity="error">Staff access is required.</Alert>;
  return (
    <ScopedAdminIntakeHubEmbedded
      key={JSON.stringify([props.organizationId, user?.sub, roles])}
      {...props}
    />
  );
}

function ScopedAdminIntakeHubEmbedded({
  organizationId,
  providers = [],
  isActive = true,
}: AdminIntakeHubEmbeddedProps) {
  const theme = useTheme();
  const payerConnectionsEnabled = useFeatureGate(FeatureGates.PAYER_CONNECTIONS);
  // The admin route and backend use Auth0 staff roles, not clinic membership roles.
  const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth0();
  const staffRoles: unknown = user?.['https://api.smartscribe.health/roles'];
  const isStaffAdmin =
    isAuthenticated && !isAuthLoading && Array.isArray(staffRoles) && staffRoles.includes('Admin');
  const location = useLocation();
  const navigate = useNavigate();
  const tabsId = useId();
  const sectionSearch = new URLSearchParams(location.search);
  const legacyPayerTabSelected =
    payerConnectionsEnabled &&
    (sectionSearch.get('intakeSection') === 'payer-connections' ||
      (sectionSearch.get('intakeStage') === 'go-live' &&
        resolveAdminStatusTab(location.search) === 'billing'));
  const selectSection = (section: 'intake' | 'payer-connections') => {
    const search = new URLSearchParams(location.search);
    if (section === 'payer-connections') {
      search.set('intakeSection', section);
      search.set('intakeStage', 'go-live');
      search.set('statusTab', 'billing');
    } else {
      search.delete('intakeSection');
      search.set('intakeStage', 'intake');
    }
    navigate({ pathname: location.pathname, search: search.toString() });
  };
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const folderInputRef = useRef<HTMLInputElement | null>(null);
  const pendingUploadItemRef = useRef<IntakeItem | undefined>(undefined);
  const { enqueueSnackbar } = useSnackbar();
  const routerCanBlock = Boolean(useContext(UNSAFE_DataRouterContext));
  const selectedUserId = resolveAdminProviderScope(location.search, providers.map(getProviderId));
  const requestedScope = new URLSearchParams(location.search).get('providerScope');
  const invalidScope = Boolean(requestedScope && !selectedUserId);
  const returnTo = safeIntakeReturnTo(new URLSearchParams(location.search).get('returnTo'));
  const [hasCredentialingDraft, setHasCredentialingDraft] = useState(false);
  const [hasServicesDraft, setHasServicesDraft] = useState(false);
  const [uploadingFileCount, setUploadingFileCount] = useState(0);
  // The pending-upload overlay is sticky beneath the sticky header, so it needs
  // the header's real height. That height is not a constant: the org services
  // control adds a row, and that row grows again with each alert it raises
  // (retired service, pause warning, fan-out failures). Measure it.
  const [headerHeight, setHeaderHeight] = useState(72);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const [hasUnsavedV2Changes, setHasUnsavedV2Changes] = useState(false);
  const hasUnsavedChanges = hasUnsavedV2Changes || hasCredentialingDraft || hasServicesDraft;
  const setSelectedUserId = (userId: string, review = false) => {
    if (
      !routerCanBlock &&
      (hasUnsavedV2Changes || hasCredentialingDraft) &&
      userId !== selectedUserId &&
      !window.confirm('Discard unsaved intake or credentialing changes and change scope?')
    )
      return;
    const search = new URLSearchParams(location.search);
    if (userId) search.set('providerScope', userId);
    else search.delete('providerScope');
    if (review) {
      search.set('intakeStage', 'intake');
      search.set('intakeStep', 'confirm-completion');
    }
    navigate({ pathname: location.pathname, search: search.toString() });
  };
  const forceV2 = hasIntakeV2DevelopmentOverride(location.search);

  // V3 belongs to the practice, independently of the staff actor, provider scope,
  // or V1/V2 support override. Never reuse a previous organization's cached data.
  const { currentData: practiceGates, error: practiceGateError } = useGetOrgGatesQuery(
    { organizationId },
    { skip: !organizationId },
  );
  // Same-scope background refetch keeps the current frame/draft mounted. A new
  // scope has no currentData; an explicit failed/disabled result falls back.
  const showV3 = Boolean(
    organizationId &&
    !practiceGateError &&
    practiceGates?.gates.find(gate => gate.gate_id === FeatureGates.INTAKE_HUB_V3)
      ?.evaluated_result,
  );
  const journeyStage = resolveJourneyStage(
    location.pathname,
    location.search,
    payerConnectionsEnabled,
  );
  const agreementScope = intakeAgreementScope(
    organizationId,
    user?.sub ?? '',
    user?.org_id,
    staffRoles,
  );
  const agreement = useAdminIntakeAgreement(
    organizationId,
    agreementScope,
    showV3 && isStaffAdmin && Boolean(user?.sub),
  );
  const payerTabSelected = showV3 ? journeyStage === 'go-live' : legacyPayerTabSelected;
  const intakeControlsHidden = !isActive || (showV3 ? journeyStage !== 'intake' : payerTabSelected);
  const statusTab = resolveAdminStatusTab(location.search);
  const [statusVisited, setStatusVisited] = useState(payerTabSelected);
  useEffect(() => {
    if (payerTabSelected) setStatusVisited(true);
  }, [payerTabSelected]);

  const {
    data: userGates,
    currentData: currentUserGates,
    isFetching: isUserGateFetching,
    error: userGateError,
  } = useGetUserGatesQuery(
    { userId: selectedUserId, organizationId },
    { skip: forceV2 || !organizationId || !selectedUserId },
  );
  const {
    data: organizationGates,
    currentData: currentOrganizationGates,
    isFetching: isOrganizationGateFetching,
    error: orgGateError,
  } = useGetOrgGatesQuery(
    { organizationId },
    { skip: forceV2 || !organizationId || Boolean(selectedUserId) },
  );
  const targetGates = selectedUserId ? userGates?.gates : organizationGates?.gates;
  const targetGateEnabled =
    targetGates?.find(gate => gate.gate_id === FeatureGates.INTAKE_HUB_V2)?.evaluated_result ??
    false;
  const isTargetGateFetching = selectedUserId ? isUserGateFetching : isOrganizationGateFetching;
  const targetGateError = selectedUserId ? userGateError : orgGateError;
  // Support escape hatch: the gate's v1 user list is hand-maintained, so a
  // v1 user missing from it evaluates to V2. The override lets admins view
  // either experience for the selected target without editing the gate.
  const [experienceOverride, setExperienceOverride] = useState<'auto' | 'v1' | 'v2'>('auto');
  useEffect(() => {
    setExperienceOverride('auto');
  }, [organizationId, selectedUserId]);
  const hasCurrentVersionDecision = Boolean(
    selectedUserId ? currentUserGates : currentOrganizationGates,
  );
  const isResolvingExperience =
    !forceV2 && experienceOverride === 'auto' && isTargetGateFetching && !hasCurrentVersionDecision;
  const showV2 =
    forceV2 || (experienceOverride === 'auto' ? targetGateEnabled : experienceOverride === 'v2');
  const versionDecisionWarning = Boolean(targetGateError) && experienceOverride === 'auto';

  // "All providers" is the organization scope; a selected provider is the
  // per-provider scope. Only the former carries the org services control.
  const isProviderScope = Boolean(selectedUserId);

  const dashboardArgs = {
    organizationId,
    userId: selectedUserId || undefined,
    ...(showV2 && selectedUserId ? { experience: 'v2' as const } : {}),
  };
  // Scoped to the selected provider, with `experience: 'v2'` so it shares a
  // cache entry with IntakeExperience's own fetch. In V2 with no provider
  // selected (the organization scope), V2's own dashboard covers that scope,
  // so skip the fetch. `currentData` (not `data`) drives branch selection so a
  // provider switch never renders against the previous provider's payload.
  const { data, currentData, error, isFetching, refetch } = useGetAdminIntakeHubDashboardQuery(
    dashboardArgs,
    {
      skip: !organizationId || isResolvingExperience || (showV2 && !selectedUserId),
    },
  );
  const verificationDashboard = useGetAdminIntakeHubDashboardQuery(
    { organizationId, experience: 'v2' },
    {
      skip: !showV3 || !isStaffAdmin || journeyStage !== 'verification',
      refetchOnMountOrArgChange: true,
    },
  );
  // Polls the document inventory while a document is still being read (see
  // `documentPolling.ts`) so a missed artifact event can't strand the list.
  const [documentPollingInterval, setDocumentPollingInterval] = useState(0);
  const {
    data: documentInventory,
    fulfilledTimeStamp: documentInventoryFetchedAt,
    refetch: refetchDocumentInventory,
  } = useGetAdminIntakeHubDocumentsQuery(dashboardArgs, {
    skip: !organizationId || !data || showV2,
    pollingInterval: documentPollingInterval,
    skipPollingIfUnfocused: true,
  });
  // The org-level control lives on the All-providers scope, where the
  // dashboard query above is skipped, so its catalog comes from the
  // definitions endpoint — which is also the set the backend validates a POST
  // against (retired services are rejected).
  const {
    data: definitionCatalog,
    isFetching: isDefinitionCatalogFetching,
    error: definitionCatalogError,
  } = useGetAdminIntakeHubDefinitionsQuery(undefined, {
    skip: !organizationId || isProviderScope,
  });
  const {
    data: orgEnrollments,
    isFetching: isOrgEnrollmentsFetching,
    error: orgEnrollmentsError,
    refetch: refetchOrgEnrollments,
  } = useGetAdminIntakeHubOrgEnrollmentsQuery(
    { organizationId },
    { skip: !organizationId || isProviderScope },
  );
  const [setOrgEnrollments, { isLoading: isSavingOrgEnrollments, data: orgEnrollmentsSaveResult }] =
    useSetAdminIntakeHubOrgEnrollmentsMutation();
  const { constraints: uploadConstraints, summary: uploadConstraintsSummary } =
    useAdminArtifactUploadConstraints();
  const { upload: uploadOrganizationFiles } = useUploadAdminOrganizationArtifacts(organizationId);
  const { pendingUploads, trackUpload, handleArtifactEvent, handleDocRefExtraction, reconcile } =
    useOptimisticUploads();
  useEffect(() => {
    reconcile(
      (documentInventory?.documents ?? []).map(documentItem => documentItem.document_reference),
    );
  }, [documentInventory?.documents, reconcile]);
  // Re-evaluated on every fetch (not just on data changes) so the age cutoff
  // in `documentInventoryPollingInterval` can end polling for a document that
  // never leaves `pending`.
  useEffect(() => {
    setDocumentPollingInterval(
      documentInventoryPollingInterval(documentInventory?.documents, pendingUploads),
    );
  }, [documentInventory?.documents, documentInventoryFetchedAt, pendingUploads]);

  const providerOptions = useMemo(
    () =>
      providers
        .filter(provider => getProviderId(provider))
        .map(provider => ({
          id: getProviderId(provider),
          label: provider.name || provider.email || getProviderId(provider),
          email: provider.email,
        })),
    [providers],
  );
  const eligibleProviders = useMemo(
    () =>
      providers
        .filter(provider => provider.roles?.includes(ROLES.CLINICIAN))
        .filter(provider => getProviderId(provider))
        .map(provider => ({
          userId: getProviderId(provider),
          name: provider.name || provider.email || getProviderId(provider),
        })),
    [providers],
  );
  const orgServiceOptions = useMemo<IntakeHubOrgServiceOption[]>(
    () =>
      (definitionCatalog?.services ?? [])
        // A retired service can't be bought: the backend validates a POST
        // against the active definitions and rejects the rest. An organization
        // already enrolled in one is handled by the control, which reports it
        // rather than POSTing a key the backend would refuse.
        .filter(service => service.status !== 'deprecated')
        // The catalog carries the order staff curate it in; respect it rather
        // than whatever order the rows arrive in.
        .toSorted((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
        .map(service => ({
          id: service.service_key as IntakeServiceId,
          label: service.label || service.service_key,
          description: service.description ?? undefined,
        })),
    [definitionCatalog?.services],
  );
  // The control cannot be trusted to act on a half-read state: without the
  // catalog it has no checkboxes and no way to tell retired from active, and
  // without the live set it has no baseline to diff against.
  const hasUnreadableOrgServices = Boolean(orgEnrollmentsError) || Boolean(definitionCatalogError);
  const enrolledOrgServiceKeys = useMemo<IntakeServiceId[]>(
    () => (orgEnrollments?.service_keys ?? []).map(serviceKey => serviceKey as IntakeServiceId),
    [orgEnrollments?.service_keys],
  );
  const scenario = useMemo(() => intakeHubDashboardResponseToScenario(data), [data]);
  // currentData (not data) here: on a provider switch, RTKQ keeps `data` at
  // the PREVIOUS provider's payload until the new fetch lands, but
  // `currentData` is undefined for that window. hasNoEnrollments gates which
  // branch renders (start-intake card vs the v2 experience), so it must be
  // computed off the target that's actually loaded — matching how
  // IntakeExperience itself reads this same query (currentData, not data).
  // Only a live row of this provider's own counts: the organization's row is
  // not theirs, and a paused row is a service the organization stopped paying
  // for. See `enrollmentScope.ts`.
  // Scoped on `selectedUserId`, not the payload's clinician id: the embed
  // already knows which provider is selected, and this branch only runs in the
  // provider scope, so there is no need to trust the response to echo it back.
  const hasNoEnrollments =
    Boolean(currentData) && !hasLiveProviderEnrollment(currentData?.enrollments, selectedUserId);
  const scopeLabel = selectedUserId
    ? (providerOptions.find(provider => provider.id === selectedUserId)?.label ??
      'Selected provider')
    : 'All providers';
  // Services are assigned once for the whole organization (GDN-3404), so the
  // control belongs to the All-providers scope and nowhere else. There is no
  // per-provider assign or start any more.
  const showOrgServiceControl = !isProviderScope;

  useEffect(() => {
    folderInputRef.current?.setAttribute('webkitdirectory', '');
    folderInputRef.current?.setAttribute('directory', '');
  }, []);

  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;
    const measure = () => setHeaderHeight(header.getBoundingClientRect().height);
    measure();
    // jsdom and older Safari have no ResizeObserver; the one-off measure above
    // still gives the right answer for a header that never changes size.
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  const handleUploadFiles = useCallback(
    async (files: File[], item?: IntakeItem) => {
      if (!organizationId) {
        enqueueSnackbar('Select an organization before uploading intake files.', {
          variant: 'warning',
        });
        return;
      }
      if (!selectedUserId) {
        enqueueSnackbar('Select a provider before uploading intake documents.', {
          variant: 'warning',
        });
        return;
      }

      const { accepted, rejected } = validateFiles(files, uploadConstraints);
      for (const rejection of rejected) {
        enqueueSnackbar(rejection.reason, { variant: 'error' });
      }
      if (accepted.length === 0) return;

      setUploadingFileCount(accepted.length);
      let outcome: Awaited<ReturnType<typeof uploadOrganizationFiles>>;
      try {
        outcome = await uploadOrganizationFiles(accepted, {
          sourceChannel: 'intake_hub_admin',
          intakeHub: item
            ? {
                intakeItemId: item.intakeItemId ?? item.id,
                requirementKey: item.requirementKey,
                conceptKey: item.conceptKey,
                serviceKey: item.serviceIds[0],
                userId: selectedUserId,
              }
            : { userId: selectedUserId },
        });
      } catch (uploadError) {
        enqueueSnackbar(formatUploadLimitError(uploadError), { variant: 'error' });
        return;
      } finally {
        setUploadingFileCount(0);
      }

      trackUpload(outcome);
      for (const duplicate of outcome?.duplicates ?? []) {
        enqueueSnackbar(formatDuplicateMessage(duplicate, 'organization'), {
          variant: 'info',
        });
      }
      if (outcome?.artifacts?.length) {
        enqueueSnackbar('Upload received. Intake Hub is extracting the details now.', {
          variant: 'success',
        });
      }
      await refetch();
      await refetchDocumentInventory();
    },
    [
      enqueueSnackbar,
      organizationId,
      refetch,
      refetchDocumentInventory,
      selectedUserId,
      trackUpload,
      uploadConstraints,
      uploadOrganizationFiles,
    ],
  );

  const handleOpenFilePicker = (item?: IntakeItem) => {
    pendingUploadItemRef.current = item;
    fileInputRef.current?.click();
  };

  const handleOpenFolderPicker = () => {
    pendingUploadItemRef.current = undefined;
    folderInputRef.current?.click();
  };

  const handleFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    void handleUploadFiles(files, pendingUploadItemRef.current);
    pendingUploadItemRef.current = undefined;
  };

  const handleArtifactStreamEvent = useCallback(
    (event: Parameters<typeof handleArtifactEvent>[0]) => {
      handleArtifactEvent(event);
      void refetch();
      void refetchDocumentInventory();
    },
    [handleArtifactEvent, refetch, refetchDocumentInventory],
  );

  useAdminOrganizationArtifactEventStream(organizationId, {
    enabled: Boolean(organizationId) && pendingUploads.length > 0,
    onEvent: handleArtifactStreamEvent,
    onDocRefExtraction: event => {
      handleDocRefExtraction(event);
      void refetch();
      void refetchDocumentInventory();
    },
  });

  const { isDragging } = useChartWideDropZone({
    enabled:
      Boolean(organizationId) &&
      Boolean(selectedUserId) &&
      !hasNoEnrollments &&
      !showV2 &&
      !intakeControlsHidden,
    onDrop: files => void handleUploadFiles(files),
  });
  const isUploadInFlight = uploadingFileCount > 0;

  const handleSaveOrgServices = async (serviceKeys: IntakeServiceId[]) => {
    if (!organizationId) return;
    let result: Awaited<ReturnType<ReturnType<typeof setOrgEnrollments>['unwrap']>>;
    try {
      result = await setOrgEnrollments({
        organizationId,
        organizationEnrollServicesRequest: { service_keys: serviceKeys },
      }).unwrap();
    } catch (saveError) {
      // A 409 means another admin's write landed between our GET and our POST.
      // Ours was rejected whole, so the live set is still theirs — reload it
      // rather than leaving the checkboxes showing a state nobody saved. The
      // error body carries no service keys, so the GET is what tells us what
      // actually persisted.
      if (isWriteConflict(saveError)) {
        void refetchOrgEnrollments();
        enqueueSnackbar(
          'Someone else changed these services first. Your change was not applied — the current set has been reloaded.',
          { variant: 'warning' },
        );
        return;
      }
      enqueueSnackbar('Unable to update Intake Hub services right now. Please try again.', {
        variant: 'error',
      });
      return;
    }
    // The organization row is written before providers are reconciled, and a
    // provider can fail on its own, so a 200 does not mean everyone landed.
    // Saying "updated" flatly would hide that; the control shows which ones.
    const failedCount = result?.failed_user_ids?.length ?? 0;
    if (failedCount > 0) {
      const reconciledCount = result?.reconciled_user_ids?.length ?? 0;
      enqueueSnackbar(
        `Organization services updated for ${reconciledCount} provider${
          reconciledCount === 1 ? '' : 's'
        }; ${failedCount} could not be updated.`,
        { variant: 'warning' },
      );
      return;
    }
    enqueueSnackbar('Organization services updated.', { variant: 'success' });
  };

  // A provider with no enrollment rows. Staff can no longer start an intake
  // for one provider — services are assigned to the whole organization, and
  // rostered providers get their rows from that. So this is informational.
  const providerNotEnrolledCard = (
    <Box sx={{ p: { xs: 1.5, md: 2 } }}>
      <Alert severity="info">
        {scopeLabel} has no Intake Hub services yet. Services are assigned to the whole
        organization: switch to All providers to enroll them, and make sure this provider is on the
        Intake Hub roster.
      </Alert>
    </Box>
  );
  // Gate the fallback (v1) and v2 branches alike during an in-flight
  // dashboard fetch for the selected provider. hasNoEnrollments now reads
  // currentData (undefined mid-flight), so without this gate BOTH branches
  // would momentarily fall through to their non-start-intake-card path —
  // v2 to a broken empty IntakeExperience, v1 to the fallback dashboard —
  // whenever a provider switch's request is still in flight. `showV2` stays
  // in the check so v2's semantics are unchanged (it never depended on
  // isFetching); `isFetching` is what makes the v1 switch-between-two-
  // unenrolled-providers window resolve to the loading indicator too,
  // instead of briefly firing the chart-wide drop zone / dashboard render
  // below (~:347) against a stale hasNoEnrollments read.
  const isResolvingDashboard =
    Boolean(selectedUserId) && !currentData && !error && (showV2 || isFetching);

  return (
    <Box sx={{ bgcolor: theme.palette.grey[100], borderRadius: 1, overflow: 'clip' }}>
      <Box
        component="input"
        ref={fileInputRef}
        type="file"
        multiple
        accept={uploadConstraints.allowed_content_types.join(',')}
        onChange={handleFileInputChange}
        sx={{ display: 'none' }}
      />
      <Box
        component="input"
        ref={folderInputRef}
        type="file"
        multiple
        accept={uploadConstraints.allowed_content_types.join(',')}
        onChange={handleFileInputChange}
        sx={{ display: 'none' }}
      />
      {(isDragging || isUploadInFlight) && (
        <ArtifactDropOverlay
          patientName="Intake Hub"
          subtitle={uploadConstraintsSummary}
          isUploading={isUploadInFlight}
          uploadingMessage={`Uploading ${uploadingFileCount} file${
            uploadingFileCount === 1 ? '' : 's'
          }`}
        />
      )}
      <Box
        ref={headerRef}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: theme.zIndex.appBar,
          bgcolor: alpha(theme.palette.background.paper, 0.9),
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          backdropFilter: 'blur(14px)',
        }}
      >
        {isFetching && <LinearProgress />}
        {payerConnectionsEnabled && !showV3 && (
          <Tabs
            value={payerTabSelected ? 'payer-connections' : 'intake'}
            onChange={(_event, section: 'intake' | 'payer-connections') => selectSection(section)}
            aria-label="Admin Intake Hub sections"
          >
            <Tab
              id={`${tabsId}-intake-tab`}
              aria-controls={`${tabsId}-intake-panel`}
              value="intake"
              label="Intake Form"
            />
            <Tab
              id={`${tabsId}-payer-tab`}
              aria-controls={`${tabsId}-payer-panel`}
              value="payer-connections"
              label="Payer connections"
            />
          </Tabs>
        )}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          sx={{
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
            px: { xs: 1.5, md: 2 },
            py: 1.25,
          }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography variant="body2" sx={{ fontWeight: 850 }}>
              Intake Hub
            </Typography>
            <Chip
              size="small"
              variant="soft"
              color={error ? 'warning' : 'success'}
              label={error ? 'Needs attention' : 'Admin'}
              sx={{ fontWeight: 800 }}
            />
          </Stack>
          {returnTo && <Button onClick={() => navigate(returnTo)}>Back to Intake Status</Button>}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}
          >
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 260 } }}>
              <InputLabel id="admin-intake-provider-scope-label" shrink>
                Scope
              </InputLabel>
              <Select
                labelId="admin-intake-provider-scope-label"
                label="Scope"
                displayEmpty
                renderValue={value =>
                  value
                    ? (providerOptions.find(provider => provider.id === value)?.label ?? value)
                    : 'All providers'
                }
                value={selectedUserId}
                onChange={event => setSelectedUserId(event.target.value)}
              >
                <MenuItem value="">All providers</MenuItem>
                {providerOptions.map(provider => (
                  <MenuItem key={provider.id} value={provider.id}>
                    <Box>
                      <Typography variant="body2">{provider.label}</Typography>
                      {provider.email && (
                        <Typography variant="caption" color="text.secondary">
                          {provider.email}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              size="small"
              variant="outlined"
              disabled={!selectedUserId || intakeControlsHidden}
              onClick={() => handleOpenFilePicker()}
            >
              Upload files
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={!selectedUserId || intakeControlsHidden}
              onClick={handleOpenFolderPicker}
            >
              Upload folder
            </Button>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={experienceOverride}
              onChange={(_event, value: 'auto' | 'v1' | 'v2' | null) => {
                if (!value || value === experienceOverride) return;
                const nextShowV2 =
                  forceV2 || (value === 'auto' ? targetGateEnabled : value === 'v2');
                if (
                  showV2 &&
                  !nextShowV2 &&
                  hasUnsavedV2Changes &&
                  !window.confirm('Discard unsaved Intake Hub changes and switch views?')
                ) {
                  return;
                }
                setExperienceOverride(value);
              }}
              aria-label="Intake experience version"
            >
              <ToggleButton value="auto" aria-label="Follow the target's feature gate">
                Auto
              </ToggleButton>
              <ToggleButton value="v1" aria-label="Force the V1 intake view">
                V1
              </ToggleButton>
              <ToggleButton value="v2" aria-label="Force the V2 intake view">
                V2
              </ToggleButton>
            </ToggleButtonGroup>
            <Button size="small" variant="text" onClick={() => navigate('/admin-v2/intake-hub')}>
              Manage definitions
            </Button>
            <Button
              size="small"
              variant="text"
              startIcon={<RefreshOutlinedIcon />}
              onClick={() => void refetch()}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>

        <Box hidden={!showOrgServiceControl} inert={!showOrgServiceControl || !isActive}>
          <Stack
            direction="row"
            sx={{
              display:
                !isActive ||
                (showV3
                  ? journeyStage !== 'agreements' && journeyStage !== 'intake'
                  : payerTabSelected)
                  ? 'none'
                  : 'flex',
              px: { xs: 1.5, md: 2 },
              pb: 1.25,
              borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
              pt: 1,
            }}
          >
            <IntakeHubOrgServiceEnrollmentControl
              key={organizationId}
              onDirtyChange={setHasServicesDraft}
              serviceOptions={orgServiceOptions}
              enrolledServiceKeys={enrolledOrgServiceKeys}
              onSave={serviceKeys => void handleSaveOrgServices(serviceKeys)}
              isSaving={isSavingOrgEnrollments}
              isLoading={isDefinitionCatalogFetching || isOrgEnrollmentsFetching}
              // Saving POSTs the COMPLETE set, so a baseline we failed to read
              // is not an empty one — it is unknown, and saving against it
              // would un-enroll everything the GET could not tell us about.
              // Either read failing is disqualifying, for the same reason:
              // saving POSTs the COMPLETE set. A baseline we could not read is
              // unknown rather than empty, and a catalog we could not read
              // cannot tell an active service from a retired one.
              isDisabled={hasUnreadableOrgServices || !isStaffAdmin}
              disabledReason={
                hasUnreadableOrgServices
                  ? 'Reload before changing services.'
                  : 'Admin access is required to change organization services.'
              }
              failedUserIds={orgEnrollmentsSaveResult?.failed_user_ids ?? []}
              errorMessage={
                orgEnrollmentsError
                  ? 'The organization services could not be loaded.'
                  : definitionCatalogError
                    ? 'The Intake Hub services could not be loaded.'
                    : undefined
              }
            />
          </Stack>
        </Box>
      </Box>
      <AdminIntakeNavigationGuard
        dirty={hasUnsavedChanges}
        scopeDirty={hasUnsavedV2Changes || hasCredentialingDraft}
      />
      {invalidScope && (
        <Alert severity="warning">
          The linked provider is not a current member of this practice. Showing All providers.
        </Alert>
      )}

      {/* Keep the form mounted across tab changes so unsaved intake edits survive. */}
      <LiveIntakeJourney
        key={agreementScope}
        enabled={showV3}
        agreementContent={
          showV3 && isStaffAdmin && agreement.status?.requires_agreement !== false ? (
            <IntakeAgreementPanel agreement={agreement} staff />
          ) : undefined
        }
        agreementStatusLabel={
          showV3 && isStaffAdmin && agreement.status?.requires_agreement !== false
            ? agreement.status?.customer_completed
              ? 'Customer signatures confirmed'
              : 'Awaiting practice admin signatures'
            : undefined
        }
        selectedStatusTab={statusTab}
        onSelectStatusTab={tab => {
          const next = journeyStageLocation(location.pathname, location.search, 'go-live');
          const search = new URLSearchParams(next.search);
          search.set('statusTab', tab);
          search.delete('intakeStatus');
          navigate({ ...next, search: search.toString() });
        }}
        keepStatusPanelsMounted
        verification={
          !isStaffAdmin ? (
            <Alert severity="info">Verification review requires Admin access.</Alert>
          ) : verificationDashboard.isError ? (
            <Alert severity="error">
              Practice verification could not be loaded. Refresh and try again.
            </Alert>
          ) : !verificationDashboard.currentData ? (
            <LinearProgress aria-label="Loading practice verification" />
          ) : (
            <AdminIntakeVerification
              providers={providerOptions}
              selectedUserId={selectedUserId}
              rosterEntries={verificationDashboard.currentData.provider_roster}
              verification={verificationDashboard.currentData.verification}
              onReview={id => setSelectedUserId(id, true)}
            />
          )
        }
        credentialingAvailable
        credentialing={
          statusVisited && showV3 ? (
            <AdminCredentialingStatuses
              organizationId={organizationId}
              userId={selectedUserId || undefined}
              enabled={showV3}
              providers={providerOptions}
              onDirtyChange={setHasCredentialingDraft}
            />
          ) : null
        }
        selectedStage={journeyStage}
        payerEnabled={payerConnectionsEnabled}
        onNavigate={stage =>
          navigate(journeyStageLocation(location.pathname, location.search, stage))
        }
        intake={
          <Box
            hidden={!showV3 && payerTabSelected}
            role={payerConnectionsEnabled && !showV3 ? 'tabpanel' : undefined}
            id={`${tabsId}-intake-panel`}
            aria-labelledby={
              payerConnectionsEnabled && !showV3 ? `${tabsId}-intake-tab` : undefined
            }
          >
            {pendingUploads.length > 0 && (
              <Box
                sx={{
                  position: 'sticky',
                  top: headerHeight,
                  zIndex: theme.zIndex.appBar - 1,
                  px: { xs: 1.5, md: 2 },
                  pt: 2,
                  pointerEvents: 'none',
                }}
              >
                <Stack spacing={0.75} sx={{ pointerEvents: 'auto' }}>
                  {pendingUploads.map(upload => (
                    <PendingUploadCard
                      key={upload.artifactId}
                      filename={upload.filename}
                      status={upload.status}
                      documentReferences={upload.documentReferences}
                      extractionSignals={upload.extractionSignals}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {versionDecisionWarning && (
              <Alert severity="warning">
                The target Intake Hub version could not be confirmed. The V1 admin view remains
                available.
              </Alert>
            )}

            {isResolvingExperience || isResolvingDashboard ? (
              <LinearProgress aria-label="Loading target Intake Hub experience" />
            ) : isProviderScope && hasNoEnrollments ? (
              providerNotEnrolledCard
            ) : showV2 ? (
              <IntakeExperience
                isActive={!intakeControlsHidden}
                adminVerificationEnabled={showV3}
                key={`${organizationId}:${selectedUserId || 'organization'}`}
                context={{
                  kind: 'admin',
                  organizationId,
                  userId: selectedUserId || undefined,
                  providerEmail: providerOptions.find(provider => provider.id === selectedUserId)
                    ?.email,
                  targetLabel: providerOptions.find(provider => provider.id === selectedUserId)
                    ?.label,
                  eligibleProviders,
                }}
                onDirtyChange={setHasUnsavedV2Changes}
              />
            ) : (
              <IntakeHubDashboard
                scenario={scenario}
                documentInventory={documentInventory?.documents ?? []}
                documentAccess={{
                  scope: 'admin',
                  organizationId,
                  userId: selectedUserId || undefined,
                }}
                onUploadRequest={selectedUserId ? handleOpenFilePicker : undefined}
              />
            )}
          </Box>
        }
        payers={
          (showV3 ? statusVisited : payerTabSelected) &&
          payerConnectionsEnabled &&
          organizationId && (
            <Box
              role={!showV3 ? 'tabpanel' : undefined}
              id={`${tabsId}-payer-panel`}
              aria-labelledby={!showV3 ? `${tabsId}-payer-tab` : undefined}
            >
              <AdminPayerConnections
                key={organizationId}
                organizationId={organizationId}
                intakeReviewPath={
                  showV3
                    ? `${location.pathname}?${new URLSearchParams({ ...Object.fromEntries(new URLSearchParams(location.search)), tab: 'intake-hub', intakeStage: 'intake' })}`
                    : undefined
                }
                onReviewIntakeClick={event => {
                  if (
                    event.button !== 0 ||
                    event.metaKey ||
                    event.ctrlKey ||
                    event.shiftKey ||
                    event.altKey
                  )
                    return;
                  event.preventDefault();
                  navigate(journeyStageLocation(location.pathname, location.search, 'intake'));
                }}
              />
            </Box>
          )
        }
      />
    </Box>
  );
}
